import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  NgZone,
  output
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { OverlayTrayService } from '../overlay-tray';
import { EmbeddedUiOverlayState, overlayPositionOf } from './embedded-ui-overlay.state';

// Fixed-position iframe shell shared by the embedded UI overlays (VictoriaLogs, service web UI).
// Docks to the host page's anchor element, covers the main column when there is none, or goes fullscreen.
// The feature wrapping it owns the dialog state and URL resolution, the shell owns positioning, the control pill and the tray bubble.
@Component({
  selector: 'z-embedded-ui-overlay',
  templateUrl: './embedded-ui-overlay.component.html',
  styleUrl: './embedded-ui-overlay.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule
  ]
})
export class EmbeddedUiOverlayComponent {

  // # Deps
  #ngZone = inject(NgZone);
  #tray = inject(OverlayTrayService);
  #sanitizer = inject(DomSanitizer);
  #destroyRef = inject(DestroyRef);

  // # Inputs
  overlay = input.required<EmbeddedUiOverlayState>();
  url = input<string | null>(null);
  loading = input(false);
  loadingText = input('Loading…');
  emptyText = input('Not available.');
  showOpenInNewTab = input(true);
  newTabTitle = input('Open in new tab');
  reloadTitle = input('Reload');
  trayId = input.required<string>();
  trayCaption = input.required<string>();
  trayTitle = input('');

  // # Outputs
  closed = output<void>();
  openInNewTab = output<void>();
  // The frame is cross-origin, so a reload means the owner fetches a fresh URL and the same element navigates to it.
  reload = output<void>();

  // # Data
  sanitizedUrl = computed(() => {
    const url = this.url();
    return url ? this.#sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });
  // Inline rect only while docked. Maximized and detached modes are pure CSS.
  dockedPosition = computed(() => {
    const s = this.overlay();
    return (s.maximized() || !s.docked()) ? null : s.position();
  });

  constructor() {
    // ResizeObserver callbacks run outside the zone, so re-enter it or OnPush views would not pick up the new rect.
    effect((onCleanup) => {
      const el = this.overlay().anchor();
      if (!el) { return; }
      const update = () => this.#ngZone.run(() => this.overlay().updatePosition(overlayPositionOf(el)));
      const observer = new ResizeObserver(update);
      observer.observe(el);
      window.addEventListener('resize', update);
      onCleanup(() => {
        observer.disconnect();
        window.removeEventListener('resize', update);
      });
    });

    // Bubble only while minimized away from the host page, the docked page has its own restore target.
    effect(() => {
      const s = this.overlay();
      if (s.open() && s.minimized() && !s.anchor()) {
        this.#tray.register({
          id: this.trayId(),
          caption: this.trayCaption(),
          title: this.trayTitle(),
          onRestore: () => s.restore(),
          onClose: () => this.closed.emit()
        });
      } else {
        this.#tray.unregister(this.trayId());
      }
    }, { allowSignalWrites: true });

    this.#destroyRef.onDestroy(() => this.#tray.unregister(this.trayId()));
  }

}
